import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import {
  Heart, Brain, Ribbon, Droplet, Loader2, Target, Utensils, AlertTriangle,
  CheckCircle2, XCircle, BookMarked, Sunrise,
} from "lucide-react";

const SPECIALTY_META = {
  cardiology: { icon: Heart,   accent: "#F87171", label: "Cardiology" },
  neurology:  { icon: Brain,   accent: "#A78BFA", label: "Neurology" },
  oncology:   { icon: Ribbon,  accent: "#FBBF24", label: "Oncology" },
  nephrology: { icon: Droplet, accent: "#4ADE80", label: "Nephrology" },
};

/**
 * DietGuidelines — Phase 3 (partial).
 * Static evidence-based per-specialty diet reference (pattern, targets,
 * eat-more/eat-less, sample meal day, red-flag conditions). Used inside the
 * multi-specialty /diet hub for Cardio / Neuro / Onco. Nephro keeps its
 * richer tracker as the primary Nephrology tab; this panel is a companion.
 */
export default function DietGuidelines({ specialty }) {
  const [guidance, setGuidance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const meta = SPECIALTY_META[specialty] || SPECIALTY_META.nephrology;

  useEffect(() => {
    setLoading(true); setErr("");
    (async () => {
      try {
        const { data } = await api.get(`/platform/diet/${specialty}`);
        setGuidance(data.guidance);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load diet guidance");
      } finally { setLoading(false); }
    })();
  }, [specialty]);

  if (loading) {
    return (
      <div className="ros-card flex items-center gap-2 text-sm text-[#A3ACA7]"
           data-testid={`diet-guidelines-loading-${specialty}`}>
        <Loader2 size={14} className="animate-spin" /> Loading {meta.label} diet guidance…
      </div>
    );
  }
  if (err || !guidance) {
    return <div className="ros-card text-sm text-[#F87171]" data-testid={`diet-guidelines-error-${specialty}`}>{err || "No guidance found."}</div>;
  }

  const g = guidance;
  return (
    <div className="space-y-4" data-testid={`diet-guidelines-${specialty}`}>
      {/* Header */}
      <div className="ros-card" style={{ borderLeftWidth: 2, borderLeftColor: meta.accent }}>
        <div className="flex items-center gap-2 mb-1">
          <BookMarked size={14} style={{ color: meta.accent }} />
          <div className="ros-label" style={{ color: meta.accent }}>Eating pattern</div>
        </div>
        <div className="font-heading text-2xl text-white">{g.pattern}</div>
        <div className="text-sm text-[#D1D5DB] mt-2 leading-relaxed">{g.description}</div>
        <div className="text-[10px] text-[#7A8480] mt-3 uppercase tracking-wider">
          Source: {g.source}
        </div>
      </div>

      {/* Targets */}
      <div className="ros-card" data-testid={`diet-targets-${specialty}`}>
        <div className="flex items-center gap-2 ros-label mb-3">
          <Target size={12} style={{ color: meta.accent }} /> Daily / weekly targets
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {g.targets.map((t) => (
            <div key={t.label} className="rounded-xl p-3"
                 style={{ background: `${meta.accent}0d`, border: `1px solid ${meta.accent}33` }}>
              <div className="text-[11px] uppercase tracking-wider text-[#A3ACA7]">{t.label}</div>
              <div className="font-heading text-lg text-white mt-0.5">{t.value}</div>
              {t.note && <div className="text-[11px] text-[#A3ACA7] mt-1 leading-snug">{t.note}</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Eat more / eat less */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="ros-card" data-testid={`diet-eat-more-${specialty}`}>
          <div className="flex items-center gap-2 ros-label mb-2" style={{ color: "#4ADE80" }}>
            <CheckCircle2 size={12} /> Eat more
          </div>
          <ul className="space-y-1.5">
            {g.eat_more.map((food, i) => (
              <li key={i} className="text-sm text-white flex items-start gap-2">
                <span className="text-[#4ADE80] mt-0.5">+</span>
                <span>{food}</span>
              </li>
            ))}
          </ul>
        </div>
        <div className="ros-card" data-testid={`diet-eat-less-${specialty}`}>
          <div className="flex items-center gap-2 ros-label mb-2" style={{ color: "#F87171" }}>
            <XCircle size={12} /> Limit / avoid
          </div>
          <ul className="space-y-1.5">
            {g.eat_less.map((food, i) => (
              <li key={i} className="text-sm text-white flex items-start gap-2">
                <span className="text-[#F87171] mt-0.5">−</span>
                <span>{food}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Sample day */}
      <div className="ros-card" data-testid={`diet-meal-pattern-${specialty}`}>
        <div className="flex items-center gap-2 ros-label mb-3">
          <Utensils size={12} style={{ color: meta.accent }} /> Sample day
        </div>
        <div className="space-y-2">
          {g.meal_pattern.map((m, i) => (
            <div key={i} className="flex items-start gap-3 text-sm"
                 data-testid={`diet-meal-${specialty}-${i}`}>
              <div className="w-24 text-[11px] uppercase tracking-wider text-[#A3ACA7] pt-0.5 flex-shrink-0 flex items-center gap-1">
                <Sunrise size={10} className="opacity-60" /> {m.meal}
              </div>
              <div className="text-white flex-1">{m.example}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Red flags */}
      <div className="ros-card" data-testid={`diet-red-flags-${specialty}`}
           style={{ background: "rgba(248,113,113,0.05)", borderColor: "rgba(248,113,113,0.35)" }}>
        <div className="flex items-center gap-2 ros-label mb-2" style={{ color: "#F87171" }}>
          <AlertTriangle size={12} /> Special considerations
        </div>
        <ul className="space-y-2">
          {g.red_flags.map((rf, i) => (
            <li key={i} className="text-sm text-[#D1D5DB] flex items-start gap-2 leading-relaxed">
              <span className="text-[#F87171] mt-0.5 font-bold">!</span>
              <span>{rf}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
